import type { EditorView } from "@tiptap/pm/view";
import type { Editor } from "@tiptap/react";
import { NodeSelection, TextSelection } from "prosemirror-state";
import {
  CONDITIONAL_NODE_NAME,
  CONTEXT_NODE_NAME,
  DATA_KEY_NODE_NAME,
  DID_NODE_NAME,
} from "./constants";

export const DYNAMIC_NODE_EDIT_EVENT = "tiptap:edit-dynamic-node";
export const DYNAMIC_NODE_EDITOR_SELECTOR = "[data-dynamic-node-editor]";

export const isEventFromCurrentTarget = (
  currentTarget: Node,
  target: EventTarget | null
): boolean => target instanceof Node && currentTarget.contains(target);

const DYNAMIC_NODE_VALUE_SELECTOR =
  ".tiptap-data-key__value, .tiptap-data-key__rich-value, .tiptap-conditional__value";

export const handleDynamicNodeMouseDown = (
  view: EditorView,
  event: MouseEvent
) => {
  const { selection } = view.state;
  if (event.button !== 0 || !(selection instanceof NodeSelection)) {
    return false;
  }
  const value =
    event.target instanceof Element
      ? event.target.closest(DYNAMIC_NODE_VALUE_SELECTOR)
      : null;
  if (value && view.nodeDOM(selection.from)?.contains(value)) {
    // Leave node-drag mode before the browser starts selecting the rendered text.
    view.dispatch(
      view.state.tr.setSelection(TextSelection.near(selection.$from))
    );
  }
  return false;
};

export const hasNativeDynamicNodeTextSelection = (
  root: Element,
  selection: Selection | null
): boolean => {
  if (!selection || selection.isCollapsed) {
    return false;
  }

  const anchor = selection.anchorNode;
  const focus = selection.focusNode;
  const anchorElement =
    anchor instanceof Element ? anchor : anchor?.parentElement;
  const value = anchorElement?.closest(DYNAMIC_NODE_VALUE_SELECTOR);

  return Boolean(
    value && focus && root.contains(value) && value.contains(focus)
  );
};

const EDITABLE_DYNAMIC_NODE_NAMES = new Set([
  DATA_KEY_NODE_NAME,
  CONTEXT_NODE_NAME,
  DID_NODE_NAME,
  CONDITIONAL_NODE_NAME,
]);

export const isEditableDynamicNodeName = (nodeName: string | undefined) =>
  Boolean(nodeName && EDITABLE_DYNAMIC_NODE_NAMES.has(nodeName));

export const getDynamicNodeEditLabel = (nodeName: string | undefined) => {
  if (nodeName === CONDITIONAL_NODE_NAME) {
    return "Edit conditional";
  }
  if (nodeName === CONTEXT_NODE_NAME) {
    return "Edit context";
  }
  if (nodeName === DID_NODE_NAME) {
    return "Edit document reference";
  }
  return "Edit data key";
};

export const requestSelectedDynamicNodeEdit = (editor: Editor) => {
  const { selection } = editor.state;
  if (
    !(selection instanceof NodeSelection) ||
    !isEditableDynamicNodeName(selection.node.type.name)
  ) {
    return false;
  }

  const nodeDom = editor.view.nodeDOM(selection.from);
  const editorElement =
    nodeDom instanceof Element
      ? nodeDom.matches(DYNAMIC_NODE_EDITOR_SELECTOR)
        ? nodeDom
        : nodeDom.querySelector(DYNAMIC_NODE_EDITOR_SELECTOR)
      : null;

  const opened =
    editorElement?.dispatchEvent(new Event(DYNAMIC_NODE_EDIT_EVENT)) ?? false;
  if (opened) {
    editor.commands.setTextSelection(selection.to);
  }
  return opened;
};
